import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import {
  buscaHistoricoAgendaDoPaciente,
  getAgendaKey,
  marcarSituacaoAgenda,
} from 'services/agenda';
import {
  cancelCall,
  createCall,
  getCallHistory,
  recallCall,
  updateCallStatus,
} from 'services/chamadas';
import { getApiErrorMessage } from 'utils/apiErrors';
import { abreviarNomePaciente } from 'utils/helpers';

const RECALL_COOLDOWN_MS = 1500;

export function usePainel({ medicoSelecionado, medicos = [], onMutate } = {}) {
  const [callStates, setCallStates] = useState({});

  // 1. Estado unificado do Modal de Histórico de Chamadas (Painel)
  const [historyState, setHistoryState] = useState({
    showModal: false,
    isLoading: false,
    events: [],
    error: '',
    agenda: null,
    call: null,
  });

  // 2. Estado unificado do Modal de Histórico de Alterações da Agenda (Paciente)
  const [agendaHistoryState, setAgendaHistoryState] = useState({
    show: false,
    loading: false,
    error: '',
    agendaEvents: [],
    pacienteEvents: [],
    pacienteNome: '',
    codAgenda: null,
  });

  const setAgendaCallState = useCallback((agendaKey, nextState) => {
    if (!agendaKey || agendaKey === 'undefined' || agendaKey === 'null') return;
    setCallStates((current) => ({
      ...current,
      [agendaKey]: {
        ...current[agendaKey],
        ...nextState,
      },
    }));
  }, []);

  // Chamar / Rechamar Paciente
  const handleCallPatient = useCallback(
    async (agenda) => {
      if (!agenda?.CODAGENDA) return;
      const agendaKey = getAgendaKey(agenda);
      if (!agendaKey || agendaKey === 'undefined') return;

      const doctorInfo = medicos.find(
        (m) => String(m.codMed) === String(agenda.CODMED || medicoSelecionado?.codMed)
      );

      const salaId = doctorInfo?.salaId || 12;
      const existingState = callStates[agendaKey];

      if (
        existingState?.isLoading ||
        (existingState?.lastRecallAt &&
          Date.now() - existingState.lastRecallAt < RECALL_COOLDOWN_MS)
      ) {
        return;
      }

      if (existingState?.callId && existingState?.status === 'calling') {
        try {
          setAgendaCallState(agendaKey, { lastRecallAt: Date.now(), isLoading: true });
          await recallCall(existingState.callId);
          setAgendaCallState(agendaKey, { status: 'calling', isRecalled: true, isLoading: false });
          toast.info('Paciente rechamado no painel.');
        } catch (err) {
          setAgendaCallState(agendaKey, { isLoading: false });
          toast.error(getApiErrorMessage(err, 'Erro ao rechamar paciente.'));
        }
        return;
      }

      try {
        setAgendaCallState(agendaKey, { isLoading: true });
        const createdCall = await createCall({
          CODAGENDA: Number(agenda.CODAGENDA),
          CODMEDICO: Number(agenda.CODMED || medicoSelecionado?.codMed),
          CODPAC: Number(agenda.CODPAC),
          NOMEPAC: abreviarNomePaciente(agenda.NOME || agenda.NOMEPAC),
          SALA: Number(salaId),
        });

        const callId = createdCall?.id || createdCall?.ID;
        setAgendaCallState(agendaKey, {
          callId,
          status: createdCall?.STATUS ? String(createdCall.STATUS).toLowerCase() : 'calling',
          isLoading: false,
          isRecalled: false,
          rawCall: createdCall,
        });

        await marcarSituacaoAgenda(agenda.CODAGENDA, 'Chamado');
        if (onMutate) await onMutate();

        toast.success('Paciente chamado no painel.');
      } catch (err) {
        setAgendaCallState(agendaKey, { isLoading: false });
        toast.error(getApiErrorMessage(err, 'Erro ao chamar paciente.'));
      }
    },
    [callStates, medicoSelecionado, medicos, onMutate, setAgendaCallState]
  );

  // Rechamar Paciente
  const handleRecallPatient = useCallback(
    async (agenda, currentCallState) => {
      const agendaKey = agenda ? getAgendaKey(agenda) : null;
      const callId = currentCallState?.callId;
      if (!callId || !agendaKey || agendaKey === 'undefined') return;

      if (
        currentCallState?.isLoading ||
        (currentCallState?.lastRecallAt &&
          Date.now() - currentCallState.lastRecallAt < RECALL_COOLDOWN_MS)
      ) {
        return;
      }

      try {
        setAgendaCallState(agendaKey, { lastRecallAt: Date.now(), isLoading: true });
        await recallCall(callId);
        setAgendaCallState(agendaKey, { status: 'calling', isRecalled: true, isLoading: false });

        if (agenda?.CODAGENDA) {
          await marcarSituacaoAgenda(agenda.CODAGENDA, 'Chamado');
        }
        if (onMutate) await onMutate();

        toast.info('Rechamada enviada para o painel.');
      } catch (err) {
        setAgendaCallState(agendaKey, { isLoading: false });
        toast.error(getApiErrorMessage(err, 'Erro ao rechamar paciente.'));
      }
    },
    [onMutate, setAgendaCallState]
  );

  // Confirmar Na Sala
  const handleArrivePatient = useCallback(
    async (agenda, currentCallState) => {
      const agendaKey = agenda ? getAgendaKey(agenda) : null;
      const callId = currentCallState?.callId;
      if (!callId || !agendaKey || agendaKey === 'undefined') return;

      try {
        setAgendaCallState(agendaKey, { isLoading: true });
        await updateCallStatus(callId, 'arrived');
        setAgendaCallState(agendaKey, { status: 'arrived', isLoading: false, isRecalled: false });
        toast.success('Chegada do paciente sinalizada no painel.');
      } catch (err) {
        setAgendaCallState(agendaKey, { isLoading: false });
        toast.error(getApiErrorMessage(err, 'Erro ao sinalizar chegada.'));
      }
    },
    [setAgendaCallState]
  );

  // Cancelar Atendimento
  const handleCancelPatient = useCallback(
    async (agenda, currentCallState) => {
      const agendaKey = agenda ? getAgendaKey(agenda) : null;
      const callId = currentCallState?.callId;
      if (!callId || !agendaKey || agendaKey === 'undefined') return;

      try {
        setAgendaCallState(agendaKey, { isLoading: true });
        await cancelCall(callId);
        setAgendaCallState(agendaKey, { status: 'canceled', isLoading: false, isRecalled: false });

        /*         if (agenda?.CODAGENDA) {
          await marcarSituacaoAgenda(agenda.CODAGENDA, 'Marcada');
        } */
        if (onMutate) await onMutate();

        toast.success('Chamada cancelada.');
      } catch (err) {
        setAgendaCallState(agendaKey, { isLoading: false });
        toast.error(getApiErrorMessage(err, 'Erro ao cancelar chamada no painel do médico.'));
      }
    },
    [onMutate, setAgendaCallState]
  );

  // Finalizar Atendimento
  const handleDonePatient = useCallback(
    async (agenda, currentCallState) => {
      const callId = currentCallState?.callId;
      const agendaKey = agenda ? getAgendaKey(agenda) : null;
      if (!callId || !agendaKey || agendaKey === 'undefined') return;

      try {
        setAgendaCallState(agendaKey, { isLoading: true });
        await Promise.all([
          updateCallStatus(callId, 'done'),
          agenda?.CODAGENDA
            ? marcarSituacaoAgenda(agenda.CODAGENDA, 'Atendido')
            : Promise.resolve(),
        ]);

        setAgendaCallState(agendaKey, { status: 'done', isLoading: false, isRecalled: false });
        if (onMutate) await onMutate();
        toast.success('Atendimento concluído.');
      } catch (err) {
        setAgendaCallState(agendaKey, { isLoading: false });
        toast.error(getApiErrorMessage(err, 'Erro ao concluir atendimento.'));
      }
    },
    [onMutate, setAgendaCallState]
  );

  // Modal de Histórico de Chamadas do Painel
  const handleViewCallHistory = useCallback(async (agenda, currentCallState) => {
    const codAgenda = agenda?.CODAGENDA;
    if (!codAgenda) {
      toast.warn('Não existe histórico para esta agenda ainda.');
      return;
    }

    try {
      setHistoryState({
        showModal: true,
        isLoading: true,
        events: [],
        error: '',
        agenda,
        call: currentCallState?.rawCall || null,
      });

      const events = await getCallHistory(codAgenda);
      setHistoryState((prev) => ({
        ...prev,
        isLoading: false,
        events,
      }));
    } catch (err) {
      const message = getApiErrorMessage(err, 'Erro ao carregar histórico da chamada.');
      setHistoryState((prev) => ({
        ...prev,
        isLoading: false,
        error: message,
      }));
    }
  }, []);

  const handleCloseHistoryModal = useCallback(() => {
    setHistoryState((prev) => ({
      ...prev,
      showModal: false,
    }));
  }, []);

  // Modal de Histórico da Agenda / Paciente
  const handleViewAgendaHistory = useCallback(
    async (codPac, codAgenda = null, pacienteNome = '') => {
      if (!codPac && !codAgenda) return;

      // 1. Abre o modal e seta o estado de loading imediatamente
      setAgendaHistoryState({
        show: true,
        loading: true,
        error: '',
        agendaEvents: [],
        pacienteEvents: [],
        pacienteNome: pacienteNome || '',
        codAgenda: codAgenda || null,
      });

      try {
        // 2. Busca paralela das alterações da agenda e do histórico completo do paciente
        const [agendaData, pacienteData] = await Promise.all([
          codAgenda ? buscaHistoricoAgendaDoPaciente(codPac, codAgenda) : Promise.resolve([]),
          codPac ? buscaHistoricoAgendaDoPaciente(codPac) : Promise.resolve([]),
        ]);

        // 3. Atualiza os dados mantendo o modal aberto
        setAgendaHistoryState((prev) => ({
          ...prev,
          loading: false,
          agendaEvents: agendaData || [],
          pacienteEvents: pacienteData || [],
        }));
      } catch (err) {
        console.error('Erro ao buscar histórico:', err);
        const message = getApiErrorMessage(err, 'Erro ao carregar histórico de alterações.');
        setAgendaHistoryState((prev) => ({
          ...prev,
          loading: false,
          error: message,
        }));
        toast.error(message);
      }
    },
    []
  );

  // Fechar Modal de Histórico de Alterações da Agenda
  const handleCloseAgendaHistoryModal = useCallback(() => {
    setAgendaHistoryState((prev) => ({
      ...prev,
      show: false,
    }));
  }, []);

  return {
    callStates,
    setCallStates,
    setAgendaCallState,
    handleCallPatient,
    handleRecallPatient,
    handleArrivePatient,
    handleDonePatient,
    handleCancelPatient,

    // Histórico de Chamadas no Painel
    handleViewCallHistory,
    handleCloseHistoryModal,
    historyState,

    // Histórico de Alterações da Agenda / Paciente (para usar no AgendaHistoryModal)
    handleViewAgendaHistory,
    handleCloseAgendaHistoryModal,
    agendaHistoryState,
  };
}
